import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Fires the function parameter exactly once when the dep is true.
 * Good for triggering an after-init effect when a "loading" flag turns true.
 */
export function useOnce(fn: () => void, dep: boolean): void {
  const isTriggered = useRef(false);

  useEffect(() => {
    if (!isTriggered.current && dep) {
      isTriggered.current = true;
      fn();
    }
  }, [isTriggered, dep, fn]);
}

/** Only calculates the value on the first call, and if fn changes.
 * Can be used to defer an expensive calculation.
 * fn expected to be memoized (useCallback) */
export function useLazyMemo<T>(fn: () => T): () => T {
  return useMemo(() => {
    let value: T;
    let computed = false;
    return () => {
      if (!computed) {
        value = fn();
        computed = true;
      }
      return value;
    };
  }, [fn]);
}

const GLOBAL_KEYBOARD_STATE: Set<KeyboardEvent['code']> = new Set();
const GLOBAL_KEYBOARD_LISTENERS = {
  down: (event: KeyboardEvent) => {
    GLOBAL_KEYBOARD_STATE.add(event.code);
  },
  up: (event: KeyboardEvent) => {
    GLOBAL_KEYBOARD_STATE.delete(event.code);
  },
};

/** Note: keys pressed down while a drag is active do not register. For a key to be available at drop, it needs to be pressed before the drag starts. */
export function useGetKeyboardState() {
  if (typeof window !== 'undefined') {
    window.addEventListener('keydown', GLOBAL_KEYBOARD_LISTENERS.down, false);
    window.addEventListener('keyup', GLOBAL_KEYBOARD_LISTENERS.up, false);
  }

  return useCallback(
    (code: KeyboardEvent['code']) => GLOBAL_KEYBOARD_STATE.has(code),
    [],
  );
}

export function useKeyboardState(code: KeyboardEvent['code']) {
  const [isDown, setIsDown] = useState(false);

  useEffect(() => {
    const downListener = (event: KeyboardEvent) => {
      if (event.code === code) {
        setIsDown(true);
      }
    };

    const upListener = (event: KeyboardEvent) => {
      if (event.code === code) {
        setIsDown(false);
      }
    };

    window.addEventListener('keydown', downListener, false);
    window.addEventListener('keyup', upListener, false);

    return () => {
      window.removeEventListener('keydown', downListener, false);
      window.removeEventListener('keyup', upListener, false);
    };
  }, [code]);

  return isDown;
}
